import React, { useState, useEffect } from "react";
import "./App.css";
import Login from "./components/Login";
import WoWSection from "./components/WoWSection";
import WoWEventsSection from "./components/WoWEventsSection";


// Composant principal App
function App() {
  const [currentSection, setCurrentSection] = useState(() => {
    const saved = localStorage.getItem("ppfbot_currentSection");
    return saved || "home";
  });
  const [wowSubSection, setWowSubSection] = useState(() => {
    const saved = localStorage.getItem("ppfbot_wowSubSection");
    return saved || "roster";
  });
  const [user, setUser] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const setUserWithLog = (newUser) => {
    setUser(newUser);
  };

  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "https://localhost:3443";

  // Fonction pour sauvegarder la session dans le localStorage
  const saveSession = (userData, session) => {
    localStorage.setItem("ppfbot_user", JSON.stringify(userData));
    localStorage.setItem("ppfbot_session", session);
  };

  // Fonction pour charger la session depuis le localStorage
  const loadSession = () => {
    const savedUser = localStorage.getItem("ppfbot_user");
    const savedSession = localStorage.getItem("ppfbot_session");

    if (savedUser && savedSession) {
      return {
        user: JSON.parse(savedUser),
        session: savedSession,
      };
    }
    return null;
  };

  // Fonction pour nettoyer la session
  const clearSession = () => {
    localStorage.removeItem("ppfbot_user");
    localStorage.removeItem("ppfbot_session");
    localStorage.removeItem("ppfbot_currentSection");
    localStorage.removeItem("ppfbot_wowSubSection");
  };

  // Fonction pour sauvegarder la section actuelle
  const saveCurrentSection = (section) => {
    localStorage.setItem("ppfbot_currentSection", section);
    setCurrentSection(section);
  };

  // Fonction pour sauvegarder la sous-section WoW
  const saveWowSubSection = (subSection) => {
    localStorage.setItem("ppfbot_wowSubSection", subSection);
    setWowSubSection(subSection);
  };

  // Vérifier la session au chargement de l'app
  useEffect(() => {
    const checkExistingSession = async () => {
      const savedSession = loadSession();

      if (savedSession) {
        try {
          // Vérifier si la session est toujours valide côté serveur
          const response = await fetch(`${API_BASE_URL}/api/auth/check`, {
            headers: {
              Authorization: `Bearer ${savedSession.session}`,
            },
          });
          const data = await response.json();

          if (data.authenticated) {
            setUserWithLog(savedSession.user);
            setSessionId(savedSession.session);
          } else {
            // Session expirée, nettoyer le localStorage
            clearSession();
          }
        } catch (error) {
          console.error("⚠️ Erreur lors de la vérification de session:", error);
          // En cas d'erreur, on garde la session locale pour éviter les déconnexions intempestives
          setUserWithLog(savedSession.user);
          setSessionId(savedSession.session);
        }
      }

      setIsLoading(false);
    };

    checkExistingSession();
  }, [API_BASE_URL]);

  const handleLogin = (userData, session) => {
    setUserWithLog(userData);
    setSessionId(session);
    saveSession(userData, session);
  };

  const handleLogout = async () => {
    if (sessionId) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
          console.error("❌ Erreur lors de la suppression de session");
        }
      } catch (error) {
        console.error("Erreur lors de la déconnexion:", error);
      }
    }

    // Nettoyer l'état local et le localStorage
    setUserWithLog(null);
    setSessionId(null);
    clearSession();

    // Rediriger vers la page de connexion
    window.location.href = "/";
  };

  // Afficher un loader pendant la vérification de session
  if (isLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Vérification de la session...</p>
      </div>
    );
  }

  // Si l'utilisateur n'est pas connecté, afficher la page de connexion
  if (!user) {
    // Vérifier une dernière fois le localStorage avant de rediriger
    const lastChanceSession = loadSession();
    if (lastChanceSession) {
      setUserWithLog(lastChanceSession.user);
      setSessionId(lastChanceSession.session);
      return (
        <div className="loading">
          <div className="spinner"></div>
          <p>Restauration de la session...</p>
        </div>
      );
    }
    return <Login onLogin={handleLogin} />;
  }

  const renderSection = () => {
    switch (currentSection) {
      case "wow":
        if (wowSubSection === "events") {
          return <WoWEventsSection sessionId={sessionId} />;
        }
        return <WoWSection sessionId={sessionId} />;
      default:
        return (
          <div className="home-section">
            <header className="main-header">
              <h1>🤖 PPFBot</h1>
              <p>Plateforme de gestion communautaire</p>
            </header>

            <div className="sections-grid">
              <div
                className="section-card wow-card"
                onClick={() => saveCurrentSection("wow")}
              >
                <div className="card-icon">🎮</div>
                <h2>World of Warcraft</h2>
                <p>
                  Gérez votre roster WoW, consultez les statistiques de votre
                  guilde et organisez vos raids.
                </p>
                <div className="card-features">
                  <span>📊 Roster en temps réel</span>
                  <span>👥 Gestion des membres</span>
                  <span>🎯 Statistiques détaillées</span>
                </div>
                <button className="section-btn">Accéder</button>
              </div>


            </div>

            <div className="back-to-home" style={{ display: "none" }}>
              <button onClick={() => setCurrentSection("home")}>
                🏠 Retour à l'accueil
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="App">
      {/* Bouton de déconnexion en haut à droite */}
      <div className="logout-container">
        <button onClick={handleLogout} className="logout-btn-top">
          <span className="logout-icon">🚪</span>
          <span className="logout-text">Déconnexion</span>
        </button>
      </div>

      <div className="container">
        {currentSection !== "home" && (
          <div className="navigation-header">
            <button
              className="back-btn"
              onClick={() => saveCurrentSection("home")}
            >
              ← Retour à l'accueil
            </button>

            {currentSection === "wow" && (
              <div className="wow-navigation">
                <button
                  className={`nav-link ${
                    wowSubSection === "roster" ? "active" : ""
                  }`}
                  onClick={() => saveWowSubSection("roster")}
                >
                  📊 Roster
                </button>
                <button
                  className={`nav-link ${
                    wowSubSection === "events" ? "active" : ""
                  }`}
                  onClick={() => saveWowSubSection("events")}
                >
                  ⚔️ Événements
                </button>
              </div>
            )}

            <div className="section-indicator">
              {currentSection === "wow" && "🎮 World of Warcraft"}

            </div>
          </div>
        )}

        {renderSection()}
      </div>
    </div>
  );
}

export default App;
